import React, { useEffect, useState } from "react"
import { navigate } from "vike/client/router"
import moment from "moment"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Value from "aldoo-ra/Value"
import TableKPICard from "aldoo-ra/Admin/CustomerSupport/TableKPICard"
import {
  ClockIcon,
  CheckCircleIcon,
  CogIcon,
  UserGroupIcon,
  DocumentTextIcon,
  HashtagIcon,
  CodeBracketIcon,
  NumberedListIcon
} from "@heroicons/react/24/outline"
import { GetStatistics } from "./api"

// KPI Card Component with consistent styling
const KPICard = ({ title, value, icon, textColor, isHighlighted = false, onClick }) => (
  <div
    className={`bg-admin_paper rounded-xl p-5 xl:p-6 shadow-sm hover:shadow-md transition-all ${
      isHighlighted ? "border border-admin_primary" : ""
    } ${onClick ? "cursor-pointer hover:border-admin_primary hover:shadow-lg" : ""}`}
    onClick={onClick}
  >
    <div>
      <Typography
        component="h2"
        variant="admin_h4"
        className="h-[72px] text-admin_text font-normal mb-4"
      >
        {title}
      </Typography>

      <div className="flex items-center justify-between mt-6">
        <Typography 
          component="output"
          variant="admin_h2" 
          className={`${textColor} mt-0`}
        >
          {value}
        </Typography>

        <div className="bg-admin_canvas rounded-lg p-3">{icon}</div>
      </div>
    </div>
  </div>
)

const ParamOverview = () => {
  const [statistics, setStatistics] = Value("param-statistics", null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  // Navigation function
  const navigateToParams = () => {
    if (typeof window !== "undefined") {
      navigate("/admin/paramio/params")
    }
  }

  const loadStatistics = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await GetStatistics()

      if (response.result) {
        setStatistics(response.data)
        setLastUpdated(new Date())
      } else {
        setError(response.error || "Failed to load statistics")
      }
    } catch (err) {
      setError(err.message || "An error occurred while loading statistics")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatistics()
  }, [])

  // Fallback when data is loading
  if (loading && !statistics) {
    return (
      <div className="animate-pulse p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-36 shadow-md"
            ></div>
          ))}
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-80 shadow-md"
            ></div>
          ))}
        </div>
      </div>
    )
  }

  // Fallback when there's an error and no previous data
  if (error && !statistics) {
    return (
      <div className="p-8">
        <div className="bg-admin_error/30 rounded-lg p-4 mb-4">
          <Typography variant="admin_txt1" className="text-admin_error mb-2">
            Error loading statistics: {error}
          </Typography>
        </div>

        <Button onClick={loadStatistics} variant="admin_primary">
          Retry
        </Button>
      </div>
    )
  }

  const stats = statistics || {}

  return (
    <div className="h-full bg-admin_canvas p-8">
      <div className="flex justify-between items-center mb-8">
        <Typography 
          component="h1" 
          variant="admin_h3"
        >
          Configuration Parameters Overview
        </Typography>

        <div className="flex items-center gap-4">
          {lastUpdated && (
            <div className="bg-admin_paper rounded-lg px-4 py-2 shadow-sm">
              <Typography variant="admin_txt1_dim">
                Last updated: {moment(lastUpdated).fromNow()}
              </Typography>
            </div>
          )}
          
          <Button
            onClick={loadStatistics}
            variant="admin_secondary"
            disabled={loading}
            className="whitespace-nowrap"
          >
            {loading ? "Refreshing..." : "Refresh Data"}
          </Button>
        </div>
      </div>


      {/* Top Row - Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {/* Total Parameters */}
        <KPICard
          title="Total Parameters"
          value={stats.totalParams || 0}
          textColor="text-admin_primary"
          isHighlighted={true}
          icon={<CogIcon className="w-6 h-6 text-admin_primary" />}
          onClick={navigateToParams}
        />

        {/* Total Changes */}
        <KPICard
          title="Total Changes"
          value={stats.totalChanges || 0}
          textColor="text-admin_error"
          icon={<ClockIcon className="w-6 h-6 text-admin_error" />}
        />

        {/* Active Authors */}
        <KPICard
          title="Active Authors"
          value={stats.totalAuthors || 0}
          textColor="text-admin_success"
          icon={<UserGroupIcon className="w-6 h-6 text-admin_success" />}
        />

        {/* String Parameters */}
        <KPICard
          title="String Parameters"
          value={stats.typeBreakdown?.string || 0}
          textColor="text-admin_warning"
          icon={<DocumentTextIcon className="w-6 h-6 text-admin_warning" />}
        />

        {/* Numeric Parameters */}
        <KPICard
          title="Numeric Parameters"
          value={stats.typeBreakdown?.number || 0}
          textColor="text-admin_text"
          icon={<HashtagIcon className="w-6 h-6 text-admin_text" />}
        />
      </div>

      {/* Secondary Statistics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        <KPICard
          title="Boolean Parameters"
          value={stats.typeBreakdown?.boolean || 0}
          textColor="text-admin_primary"
          icon={<CheckCircleIcon className="w-6 h-6 text-admin_primary" />}
        />

        <KPICard
          title="Object Parameters"
          value={stats.typeBreakdown?.object || 0}
          textColor="text-admin_progress"
          icon={<CodeBracketIcon className="w-6 h-6 text-admin_progress" />}
        />

        <KPICard
          title="Array Parameters"
          value={stats.typeBreakdown?.array || 0}
          textColor="text-admin_success"
          icon={<NumberedListIcon className="w-6 h-6 text-admin_success" />}
        />

        <KPICard
          title="Mixed Types"
          value={stats.typeBreakdown?.mixed || 0}
          textColor="text-admin_text_secondary"
          icon={<CogIcon className="w-6 h-6 text-admin_text_secondary" />}
        />
      </div>

      {/* Bottom Row - Contributors Table */}
      <div className="grid grid-cols-1 gap-6">
        {/* Top Contributors Table */}
        <TableKPICard
          title="Top Contributors"
          data={stats.authorStats?.map(author => ({
            name: author._id,
            totalActivity: author.totalActivity,
            creationCount: author.creationCount,
            changeCount: author.changeCount
          })) || []}
          keyField="name"
          valueField="totalActivity"
          emptyMessage="No contributors yet"
          bgColor="bg-admin_paper"
          textColor="text-admin_text"
          icon={<UserGroupIcon className="w-6 h-6 text-admin_primary" />}
          maxItems={10}
          loading={loading}
          valueRenderer={(item) => (
            <div className="text-right">
              <div className="text-admin_primary font-semibold">{item.totalActivity} total</div>
              <div className="text-xs text-admin_text_secondary">
                {item.creationCount} created, {item.changeCount} modified
              </div>
            </div>
          )}
        />
      </div>

      {/* Show error as a banner if refreshing fails but we have old data */}
      {error && (
        <div className="mt-8 bg-admin_error/10 text-admin_error p-4 rounded-lg flex justify-between items-center shadow-md">
          <span>Error refreshing: {error}</span>
          
          <Button onClick={loadStatistics} variant="admin_error">
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}

export default ParamOverview